
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import {
    AlertCircle,
    CheckCircle,
    AlertTriangle,
    Loader2,
    ArrowRight,
    RefreshCw,
    Shield
} from 'lucide-react';

interface ReconciliationModalProps {
    campaignId: number;
    isOpen: boolean;
    onClose: () => void;
}

interface ReconciliationChange {
    packId: number;
    packName: string;
    storeId: number;
    storeName: string;
    mediaFormatId: number;
    mediaFormatName: string;
    currentOperator: { id: number; name: string };
    newOperator: { id: number; name: string };
    source: 'system' | 'manual';
    action: 'UPDATE' | 'PRESERVE';
    reason: string;
}

interface ReconciliationDiff {
    campaignId: number;
    changes: ReconciliationChange[];
    stats: {
        totalAssignments: number;
        totalDifferences: number;
        systemUpdates: number;
        preservedManual: number;
    };
}

export function ReconciliationModal({ campaignId, isOpen, onClose }: ReconciliationModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [step, setStep] = useState<'PREVIEW' | 'SUCCESS'>('PREVIEW');

    // Reset step on open
    useEffect(() => {
        if (isOpen) setStep('PREVIEW');
    }, [isOpen]);

    // Fetch Preview
    const {
        data: diff,
        isLoading: isLoadingPreview,
        error: previewError
    } = useQuery<ReconciliationDiff>({
        queryKey: ['reconciliation-preview', campaignId],
        queryFn: async () => {
            const res = await apiRequest(`/api/campaigns/${campaignId}/reconcile/preview`, 'POST');
            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to generate preview');
            }
            return res.json();
        },
        enabled: isOpen,
        refetchOnWindowFocus: false
    });

    // Apply Mutation
    const applyMutation = useMutation({
        mutationFn: async () => {
            const res = await apiRequest(`/api/campaigns/${campaignId}/reconcile/apply`, 'POST');
            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to apply changes');
            }
            return res.json();
        },
        onSuccess: () => {
            setStep('SUCCESS');
            queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}`] });
            queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
            queryClient.invalidateQueries({ queryKey: ['snapshot-edit', campaignId] });
        },
        onError: (error: Error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        }
    });

    const handleConfirm = () => {
        applyMutation.mutate();
    };

    const handleClose = () => {
        applyMutation.reset();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0">
                <DialogHeader className="p-6 pb-4 border-b">
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="h-5 w-5 text-blue-600" />
                        Reconcile Operator Assignments
                    </DialogTitle>
                    <DialogDescription>
                        Review and apply system-driven operator updates. Manual overrides will be safely preserved.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6">
                    {isLoadingPreview ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-4">
                            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                            <p className="text-muted-foreground">Analyzing operator assignments...</p>
                        </div>
                    ) : previewError ? (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>{(previewError as Error).message}</AlertDescription>
                        </Alert>
                    ) : step === 'SUCCESS' ? (
                        <div className="flex flex-col items-center justify-center py-8 space-y-6 text-center">
                            <div className="rounded-full bg-green-100 p-3">
                                <CheckCircle className="h-12 w-12 text-green-600" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-semibold">Reconciliation Complete</h3>
                                <p className="text-muted-foreground max-w-md mx-auto">
                                    Operator assignments have been updated.
                                    <br />
                                    <strong>{diff?.stats.systemUpdates}</strong> system changes applied.
                                    <br />
                                    <strong>{diff?.stats.preservedManual}</strong> manual overrides preserved.
                                </p>
                            </div>
                        </div>
                    ) : diff ? (
                        <div className="space-y-6">
                            {/* Summary Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-center">
                                    <div className="text-2xl font-bold text-blue-700">{diff.stats.systemUpdates}</div>
                                    <div className="text-sm text-blue-600 font-medium">Updates Available</div>
                                    <div className="text-xs text-blue-400 mt-1">System source</div>
                                </div>
                                <div className="bg-amber-50 p-4 rounded-lg border border-amber-100 text-center">
                                    <div className="text-2xl font-bold text-amber-700">{diff.stats.preservedManual}</div>
                                    <div className="text-sm text-amber-600 font-medium">Preserved</div>
                                    <div className="text-xs text-amber-400 mt-1">Manual source</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                                    <div className="text-2xl font-bold text-gray-700">{diff.stats.totalDifferences}</div>
                                    <div className="text-sm text-gray-600 font-medium">Total Differences</div>
                                    <div className="text-xs text-gray-400 mt-1">Found in snapshot</div>
                                </div>
                            </div>

                            {diff.changes.length === 0 ? (
                                <Alert className="bg-green-50 border-green-200">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <AlertDescription className="text-green-800 font-medium">
                                        All assignments are up to date! No changes needed.
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <div className="border rounded-md">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Store & Format</TableHead>
                                                <TableHead>Current Operator</TableHead>
                                                <TableHead>System Operator</TableHead>
                                                <TableHead className="text-right">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {diff.changes.map((change, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>
                                                        <div className="font-medium">{change.storeName}</div>
                                                        <div className="text-xs text-muted-foreground">{change.mediaFormatName}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={change.action === 'UPDATE' ? 'text-red-500 line-through' : ''}>
                                                            {change.currentOperator.name}
                                                        </span>
                                                        {change.action === 'PRESERVE' && (
                                                            <div className="text-xs flex items-center gap-1 text-amber-600 mt-1">
                                                                <Shield className="h-3 w-3" />
                                                                Manual Override
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            {change.action === 'UPDATE' && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                                                            <span className={change.action === 'UPDATE' ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                                                                {change.newOperator.name}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {change.action === 'UPDATE' ? (
                                                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Update</Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">Preserve</Badge>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>

                <DialogFooter className="p-6 pt-4 border-t bg-gray-50">
                    {step === 'SUCCESS' ? (
                        <Button onClick={handleClose}>Close</Button>
                    ) : (
                        <div className="flex gap-2 w-full justify-end">
                            <Button variant="outline" onClick={handleClose}>Cancel</Button>
                            <Button
                                onClick={handleConfirm}
                                disabled={!diff || diff.changes.length === 0 || diff.stats.systemUpdates === 0 || applyMutation.isPending}
                            >
                                {applyMutation.isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Applying...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Confirm & Apply Changes
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
